import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import './OrderConfirmation.css';

const OrderConfirmation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const [userPhone, setUserPhone] = useState('');
  const [whatsappSent, setWhatsappSent] = useState(false);
  
  // Get order data from location state
  const { 
    paymentMethod, 
    total, 
    items = [], 
    customerPhone,
    orderId,
    customerName,
    deliveryAddress 
  } = location.state || {};

  // Company WhatsApp number
  const companyWhatsAppNumber = '918861938913'; // Added country code for India

  useEffect(() => {
    // Define formatOrderItems inside useEffect
    const formatOrderItems = () => {
      if (!items || items.length === 0) {
        return 'Order details not available';
      }

      return items.map((item, index) => {
        const itemName = item.name || item.productName || 'Unknown Item';
        const itemPrice = item.price || item.finalPrice || 0;
        const itemQuantity = item.quantity || 1;
        const itemTotal = itemPrice * itemQuantity;
        
        return `${index + 1}. ${itemName}
   Price: ₹${itemPrice.toLocaleString('en-IN')}
   Qty: ${itemQuantity}
   Total: ₹${itemTotal.toLocaleString('en-IN')}`;
      }).join('\n\n');
    };

    // Define sendWhatsAppMessage inside useEffect to avoid dependency issues
    const sendWhatsAppToCompany = (phoneNumber) => {
      if (whatsappSent) return; // Prevent multiple sends
      
      try {
        // Always send to company WhatsApp number, not customer number
        const formattedPhone = companyWhatsAppNumber;

        const orderDate = new Date().toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });

        // Create detailed WhatsApp message for company
        const message = `🎉 *NEW ORDER RECEIVED*

📋 *Order Details:*
Order ID: ${orderId || 'N/A'}
Date: ${orderDate}
Payment Method: ${paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment'}

👤 *Customer Details:*
Name: ${customerName || 'Not provided'}
Phone: ${phoneNumber || 'Not provided'}

🛍️ *Items Ordered:*
${formatOrderItems()}

💰 *Total Amount: ₹${total ? total.toLocaleString('en-IN') : '0.00'}*

📍 *Delivery Address:*
${deliveryAddress || 'Address not provided'}

🚚 Please process this order according to the selected delivery options.

---
ProCart Order Management System`;

        // Encode message for URL
        const encodedMessage = encodeURIComponent(message);
        
        // Create WhatsApp URL
        const whatsappURL = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
        
        // Open WhatsApp in new tab
        window.open(whatsappURL, '_blank');
        setWhatsappSent(true);
        
        console.log('WhatsApp message sent to company:', formattedPhone);
      } catch (error) {
        console.error('Error sending WhatsApp message:', error);
      }
    };

    // Get user phone number and send WhatsApp message
    const fetchUserDetailsAndSendWhatsApp = async () => {
      try {
        let phoneNumber = customerPhone;
        
        // If phone number not in state, try to get from Firebase Auth user
        if (!phoneNumber && auth.currentUser) {
          const userDocRef = doc(db, 'users', auth.currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            phoneNumber = userData.phone || userData.phoneNumber;
          }
        }

        if (phoneNumber) {
          setUserPhone(phoneNumber);
        }
        
        // Always send WhatsApp message to company after a short delay to ensure page loads
        setTimeout(() => {
          sendWhatsAppToCompany(phoneNumber || ''); // Send user phone for inclusion in message, but message goes to company
        }, 1000);
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    };

    fetchUserDetailsAndSendWhatsApp();
  }, [auth.currentUser, customerPhone, orderId, paymentMethod, customerName, total, deliveryAddress, items, whatsappSent, companyWhatsAppNumber]);

  const handleContinueShopping = () => {
    navigate('/products');
  };

  const handleViewOrders = () => {
    navigate('/orders');
  };

  const handleResendWhatsApp = () => {
    // Reset the flag and trigger a manual send to company
    setWhatsappSent(false);
    
    const phoneNumber = userPhone || '';
    const formattedPhone = companyWhatsAppNumber;

    const orderDate = new Date().toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Format order items inline
    const formattedItems = items && items.length > 0 
      ? items.map((item, index) => {
          const itemName = item.name || item.productName || 'Unknown Item';
          const itemPrice = item.price || item.finalPrice || 0;
          const itemQuantity = item.quantity || 1;
          const itemTotal = itemPrice * itemQuantity;
          const deliveryType = item.deliverySpeed ? 
            (item.deliverySpeed === 'quick' ? '⚡ Quick' : 
             item.deliverySpeed === 'late' ? '🐌 Eco' : '🚚 Standard') : '';
          
          return `${index + 1}. ${itemName}
   Price: ₹${itemPrice.toLocaleString('en-IN')}
   Qty: ${itemQuantity}
   Delivery: ${deliveryType}
   Total: ₹${itemTotal.toLocaleString('en-IN')}`;
        }).join('\n\n')
      : 'Order details not available';

    // Create detailed WhatsApp message for company
    const message = `🎉 *NEW ORDER RECEIVED*

📋 *Order Details:*
Order ID: ${orderId || 'N/A'}
Date: ${orderDate}
Payment Method: ${paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment'}

👤 *Customer Details:*
Name: ${customerName || 'Not provided'}
Phone: ${phoneNumber || 'Not provided'}

🛍️ *Items Ordered:*
${formattedItems}

💰 *Total Amount: ₹${total ? total.toLocaleString('en-IN') : '0.00'}*

📍 *Delivery Address:*
${deliveryAddress || 'Address not provided'}

🚚 Please process this order according to the selected delivery options.

---
ProCart Order Management System`;

    // Encode message for URL
    const encodedMessage = encodeURIComponent(message);
    
    // Create WhatsApp URL
    const whatsappURL = `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
    
    // Open WhatsApp in new tab
    window.open(whatsappURL, '_blank');
    setWhatsappSent(true);
  };

  return (
    <div className="confirmation-container">
      <div className="confirmation-box">
        <svg
          className="success-icon"
          width="80"
          height="80"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        
        <h2>Order Placed Successfully!</h2>
        
        <div className="order-summary">
          <p>
            Your order has been placed successfully with{' '}
            {paymentMethod === 'cod' ? 'Cash on Delivery' : 'Online Payment'}.
          </p>
          
          {orderId && (
            <p className="order-id">
              <strong>Order ID:</strong> {orderId}
            </p>
          )}
          
          <p className="total-amount">
            <strong>Total Amount: ₹{total ? total.toLocaleString('en-IN') : '0.00'}</strong>
          </p>

          {items && items.length > 0 && (
            <div className="items-summary">
              <h3>Items Ordered:</h3>
              <ul>
                {items.map((item, index) => (
                  <li key={index}>
                    {item.name || item.productName} - 
                    Qty: {item.quantity || 1} - 
                    ₹{((item.price || item.finalPrice || 0) * (item.quantity || 1)).toLocaleString('en-IN')}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="whatsapp-status">
          {whatsappSent ? (
            <div className="whatsapp-sent">
              <span className="whatsapp-icon">✅</span>
              <p>Order details sent to ProCart team via WhatsApp!</p>
            </div>
          ) : userPhone ? (
            <div className="whatsapp-sending">
              <span className="whatsapp-icon">📱</span>
              <p>Sending order details to ProCart team...</p>
            </div>
          ) : (
            <div className="whatsapp-info">
              <span className="whatsapp-icon">ℹ️</span>
              <p>Order details will be sent to ProCart team</p>
            </div>
          )}
        </div>

        <div className="action-buttons">
          <button className="continue-btn" onClick={handleContinueShopping}>
            Continue Shopping
          </button>
          
          <button className="view-orders-btn" onClick={handleViewOrders}>
            View My Orders
          </button>
          
          <button className="resend-whatsapp-btn" onClick={handleResendWhatsApp}>
            📱 Send to ProCart Team
          </button>
        </div>

        <div className="support-info">
          <p>
            Need help? Contact us on WhatsApp: 
            <a 
              href={`https://wa.me/${companyWhatsAppNumber}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="support-link"
            >
              +91 9606271260
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
